import { DEFAULT_TEMPLATES } from 'features/load-hub/components/expanded-row/OfferBookEmailCompose'
import type { ComposeEmailTemplateDefinition } from 'features/load-hub/components/expanded-row/OfferBookEmailCompose'
import type { ComposeEmailType } from 'features/load-hub/components/expanded-row/savedTemplate'
import type { HtmlPreviewRoleResolver } from '@/components/tiptap/extensions/PreservedHtml'

/**
 * The Box Truck board's own bundled email templates.
 *
 * Separate from Load Hub's DEFAULT_TEMPLATES on purpose: that object is shared, and this board's outbound
 * mail is a bid card rather than a plain-text inquiry. Only `offer` differs — Info and Book fall through to
 * the shared defaults, so there is one copy of each of those, not two that can drift.
 *
 * The card is one self-contained unit: it carries its own company footer instead of leaning on the
 * signature block, which would print the company a second time underneath it. The operator's own logo still
 * reaches it — the compose lifts the logo out of the resolved signature and exposes it as {{company_logo}},
 * so nothing here hard-codes an image URL.
 *
 * Inline styles throughout, and tables avoided: email clients strip <style> blocks, and these are the
 * declarations that survive Gmail and Outlook.
 */

/** Widths and colours repeated across the blocks — kept here so the card stays visually consistent. */
const CARD = 'max-width:300px;margin:10px auto;'
const PANEL = 'background-color:#ffffff;border:1px solid #e0e0e0;border-radius:8px;padding:15px;margin-bottom:10px;'
const PANEL_TITLE = 'margin:0 0 8px 0;padding-bottom:5px;border-bottom:1px solid #eee;color:#999;font-size:12px;letter-spacing:1px;text-transform:uppercase;'
const LINE = 'margin:5px 0;font-size:13px;'

const directChildren = (element: HTMLElement, tagName: string): HTMLElement[] =>
    Array.from(element.children).filter((child): child is HTMLElement => child instanceof HTMLElement && child.tagName === tagName)

/** The legacy and current Offer cards share a stable four-section structure. Keeping this classifier beside
 * the template prevents the generic Tiptap extension from knowing Box Truck copy, dimensions, or styles. */
const resolveOfferPreviewRole: HtmlPreviewRoleResolver = (element) => {
    let card: HTMLElement | null = element
    while (card) {
        const sections = directChildren(card, 'DIV')
        const paragraphCounts = sections.map((section) => directChildren(section, 'P').length)
        if (sections.length === 4 && paragraphCounts[0] === 3 && paragraphCounts[1] >= 5 && paragraphCounts[2] >= 3) break
        card = card.parentElement
    }
    if (!card) return null
    if (element === card) return 'card'

    const sections = directChildren(card, 'DIV')
    const sectionIndex = sections.findIndex((section) => section === element || section.contains(element))
    if (sectionIndex < 0 || sectionIndex > 2) return null

    const section = sections[sectionIndex]
    if (element === section) return sectionIndex === 0 ? 'panel offer-panel' : sectionIndex === 1 ? 'panel' : 'panel secondary'
    if (element.tagName !== 'P') return null

    const paragraphIndex = directChildren(section, 'P').indexOf(element)
    if (sectionIndex === 0) return ['muted', 'success', 'danger'][paragraphIndex] ?? null
    if (paragraphIndex === 0) return 'section-title'
    if (sectionIndex === 2 && paragraphIndex === 1) return 'foreground'
    return null
}

/**
 * The offer card. `{{rate}}` is the price the operator typed into the bid calculator — the compose passes it
 * as `rateOverride`, so it re-interpolates as they type.
 *
 * The driver-scoped tokens ({{miles_out}}, {{driver_dimensions}}) always resolve for this board — the compose
 * fills them with '-' when no driver is picked, so the card can never reach a broker showing raw braces.
 */
const OFFER_CARD_BODY = [
    `<div style="${CARD}">`,
    // Offer — the one number the broker is looking for, so it leads and nothing competes with it.
    '<div style="background-color:#ffffff;border:2px solid #009c00;border-radius:6px;padding:6px 12px;margin-bottom:10px;text-align:center;">',
    '<p style="margin:0;color:#666;font-size:9px;letter-spacing:0.5px;text-transform:uppercase;">Offer</p>',
    '<p style="margin:0;color:#009c00;font-size:20px;"><strong>${{rate}}</strong></p>',
    '<p style="margin:0;color:#b40000;font-size:9px;"><strong>Expires in 15 min</strong></p>',
    '</div>',
    // Load details — what the broker checks the offer against.
    `<div style="${PANEL}">`,
    `<p style="${PANEL_TITLE}">Load Details</p>`,
    `<p style="${LINE}"><strong>Miles out:</strong> {{miles_out}}</p>`,
    `<p style="${LINE}"><strong>Dimensions:</strong> {{driver_dimensions}}</p>`,
    `<p style="${LINE}"><strong>MC:</strong> {{mc}}</p>`,
    `<p style="${LINE}"><strong>USDOT:</strong> {{dot}}</p>`,
    '</div>',
    // Dispatcher contact — who to reply to. Greyed panel so it reads as secondary to the offer.
    '<div style="background-color:#f9f9f9;border:1px solid #e0e0e0;border-radius:8px;padding:15px;margin-bottom:10px;">',
    '<p style="margin:0 0 8px 0;padding-bottom:5px;border-bottom:1px solid #ddd;color:#999;font-size:12px;letter-spacing:1px;text-transform:uppercase;">Dispatcher Contact</p>',
    '<p style="margin:5px 0;color:#2c3e50;font-size:14px;"><strong>{{user_name}}</strong></p>',
    '<p style="margin:3px 0;font-size:13px;">&#128222; {{phone}}</p>',
    '<p style="margin:3px 0;font-size:13px;">&#128231; <a href="mailto:{{user_email}}" style="color:#0066cc;text-decoration:none;">{{user_email}}</a></p>',
    '</div>',
    // Company footer, inside the card rather than in the signature block — the card is a self-contained
    // unit and a separate signature underneath would print the company twice. {{company_logo}} expands to
    // a centred <img>, or to nothing at all when the operator has not uploaded one.
    '<div style="background-color:#2c3e50;border-radius:8px;padding:12px;text-align:center;">',
    '{{company_logo}}',
    '<p style="margin:0;color:#ffffff;font-size:11px;"><strong>{{company}}</strong></p>',
    '<p style="margin:3px 0;color:#bdc3c7;font-size:9px;">{{address}}</p>',
    '</div>',
    '</div>',
].join('')

/**
 * Box Truck's bundled templates. Info and Book are the shared defaults verbatim — this board only replaces
 * the offer, which is the mail it actually sends.
 */
export const BOX_TRUCK_DEFAULT_TEMPLATES: Record<ComposeEmailType, ComposeEmailTemplateDefinition> = {
    ...DEFAULT_TEMPLATES,
    offer: {
        subject: 'Quote ${{rate}} for {{origin}} to {{destination}} on {{pickup_date}} ({{trip_miles}} mi)',
        body: OFFER_CARD_BODY,
        previewRoleResolver: resolveOfferPreviewRole,
        // The SHARED signature, unchanged. The card carries its own footer, but suppressing the signature
        // belongs to the default state (via hideSignature), not to this template: blanking it here would
        // also empty the signature editor for an operator who PICKS the Offer tab and has none saved.
        // Leaving it also gives the compose a signature to lift {{company_logo}} out of.
        signature: DEFAULT_TEMPLATES.offer.signature,
    },
}
